/**
 * `/agents/runs/$id` — live worker-run trace (port of app/agents/runs/[id]/page.tsx).
 */

import { createRoute } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardContent,
  LiveRunTrace,
  ObjectKeyFacts,
  type ObjectKeyFact,
  ObjectPage,
  RelationshipPanel,
  SectionTitle,
  formatDateTime,
  formatUsd,
  roleLabel,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { RouterLink as Link } from "@excellent/ui/router";
import { agentRuns } from "@excellent/ui/api/contracts/agent-runs";
import { rootRoute } from "./__root";

const humanize = (s: string) =>
  s.replace(/[_-]+/g, " ").replace(/^./, (c) => c.toUpperCase());

function runStateIntent(status: string) {
  return status === "completed"
    ? ("success" as const)
    : status === "failed"
      ? ("danger" as const)
      : status === "aborted" || status === "budget_exceeded"
        ? ("warning" as const)
        : status === "running"
          ? ("info" as const)
          : ("neutral" as const);
}

function fmtDuration(ms: number | null): string | null {
  if (ms == null || ms < 0) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) {
    const r = s % 60;
    return r ? `${m}m ${r}s` : `${m}m`;
  }
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

function AgentRunDetailPage() {
  const { id } = agentsRunDetailRoute.useParams();
  const runId = Number(id);
  const idOk = Number.isFinite(runId) && runId > 0;
  // `useApi(op, null)` is SWR's "don't fetch" key — a malformed `$id` must not
  // issue a doomed `/api/agents/runs/NaN` before the guard below renders.
  const { data, error, isLoading } = useApi(
    agentRuns.get,
    idOk ? { params: { id: runId } } : null,
  );

  if (!idOk) {
    return (
      <div className="px-6 py-8 type-body text-destructive">Invalid run id.</div>
    );
  }

  if (isLoading && !data) {
    return (
      <div className="px-6 py-8 type-body text-muted-foreground">
        Loading run…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="px-6 py-8 type-body text-destructive" data-testid="run-not-found">
        {error?.message ?? "Run not found."}
      </div>
    );
  }

  const run = data.run;
  const processHost = run.processHostLabel;
  const codingAgentLabel = run.codingAgentLabel;
  const durationMs =
    run.wall_ms ??
    (run.finished_at
      ? new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()
      : null);
  const duration = fmtDuration(durationMs);

  const facts: ObjectKeyFact[] = [
    {
      label: "Coding agent",
      value: codingAgentLabel
        ? `${codingAgentLabel} (${roleLabel(run.role)})`
        : `${processHost} (${roleLabel(run.role)})`,
      person: {
        name: codingAgentLabel ?? processHost,
        kind: "agent",
      },
      hint: codingAgentLabel
        ? `Process host: ${processHost}`
        : "Process-tree identity (coding agent not stamped)",
    },
    {
      label: "Model provider",
      value: run.model_provider ?? "—",
    },
    { label: "Model", value: run.model ?? "—" },
    { label: "Started", value: formatDateTime(run.started_at) },
  ];
  if (duration) facts.push({ label: "Duration", value: duration });
  if (run.cost_usd != null)
    facts.push({ label: "Cost", value: formatUsd(run.cost_usd) });
  if (run.tasks_shipped || run.tasks_verified || run.tasks_claimed)
    facts.push({
      label: "Tasks",
      value: `${run.tasks_verified} verified`,
      hint: `${run.tasks_shipped} shipped · ${run.tasks_claimed} claimed`,
    });

  const goalHref = run.goal_id ? `/agents/goals/${run.goal_id}` : null;

  return (
    <div className="px-6 py-8" data-testid="agent-run-detail">
      <ObjectPage
        title={`Run #${run.id}`}
        kind="Agent run"
        state={run.status}
        stateLabel={humanize(run.status)}
        stateIntent={runStateIntent(run.status)}
        subtitle={`Agent run · ${humanize(run.status)}`}
        keyFacts={<ObjectKeyFacts facts={facts} />}
        summary={
          <LiveRunTrace
            run={{
              id: run.id,
              role: run.role,
              agent_id: run.agent_id,
              model: run.model,
              status: run.status,
              control_state: run.control_state,
              started_at: run.started_at,
              finished_at: run.finished_at,
              exit_reason: run.exit_reason,
            }}
            initialEvents={data.events}
            steps={data.steps}
            embedded
          />
        }
        actions={
          <Card className="gap-0 overflow-hidden py-0">
            <SectionTitle title="Actions" />
            <CardContent className="flex flex-col gap-2 py-4">
              {goalHref ? (
                <Button asChild variant="outline" className="w-full justify-center">
                  <Link href={goalHref}>Open goal</Link>
                </Button>
              ) : null}
              <Button asChild variant="outline" className="w-full justify-center">
                <Link href="/agents/runs">All runs</Link>
              </Button>
            </CardContent>
          </Card>
        }
        relationships={
          goalHref ? (
            <RelationshipPanel
              groups={[
                {
                  label: "Goal",
                  items: [
                    {
                      key: `goal-${run.goal_id}`,
                      label: run.goal_title ?? `Goal #${run.goal_id}`,
                      href: goalHref,
                    },
                  ],
                },
              ]}
            />
          ) : undefined
        }
      />
    </div>
  );
}

export const agentsRunDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/runs/$id",
  component: AgentRunDetailPage,
});
